import { FC, useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';

import { useForm } from '@tanstack/react-form';
import Countdown from 'react-countdown';

import {
  extractStringFromRandomLink,
  timerMinutesRender,
} from '@skillarea/lib-shared';

import { TActivationTexts } from '../../types';

import styles from './Widgets.module.scss';
import {
  useToaster,
  TextBlock,
  ButtonText,
  Button,
  SubmitButtonWrapper,
  Title,
  Expander,
} from '../../components';
import { EOnboardRoutes } from '../index';
import { handleErrorsAndTimer } from './helpers/handleErrorsAndTimer';

function getResultStatus(result: unknown): number | undefined {
  if (!result || typeof result !== 'object' || !('status' in result)) {
    return undefined;
  }

  return typeof result.status === 'number' ? result.status : undefined;
}

function getResultError(result: unknown): string | undefined {
  if (!result || typeof result !== 'object' || !('error' in result)) {
    return undefined;
  }

  return typeof result.error === 'string' ? result.error : undefined;
}

export const Activation: FC<{
  texts: TActivationTexts;
  onboardStore: any;
  onActivationSuccess?: (method: string, activationLink: string) => void;
}> = ({ texts, onboardStore, onActivationSuccess }) => {
  const [isError, setIsError] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToaster();

  let linkActivation = '';
  if (typeof window !== 'undefined') {
    const hash = window.location.hash;
    if (hash.startsWith('#activate/'))
      linkActivation = hash.replace('#activate/', '');
  }

  const email = extractStringFromRandomLink(linkActivation);

  const [resendAfter, setResendAfter] = useState<Date | null>(null);
  const lastAttemptedLinkRef = useRef<string | null>(null);
  const lastSuccessfulLinkRef = useRef<string | null>(null);

  useEffect(() => {
    if (!linkActivation) {
      void navigate({ to: location.pathname, hash: EOnboardRoutes.register });
    } else if (!email) {
      void navigate({ to: location.pathname, hash: EOnboardRoutes.register });
    }
  }, [email, linkActivation, location.pathname, navigate]);

  const form = useForm({
    defaultValues: {},
    onSubmit: async () => {
      const result = await onboardStore
        .getState()
        .resendLinkActivation({ email });
      if (result.success) {
        toast.success(texts.newSent);
        setResendAfter(new Date(Date.now() + result.timer));
      } else {
        // Handle errors for tanstack form
        console.error('Form submission error:', result);
      }
    },
  });

  useEffect(() => {
    if (!linkActivation || lastAttemptedLinkRef.current === linkActivation)
      return;
    lastAttemptedLinkRef.current = linkActivation;

    (async () => {
      const isOnlyEmailInLink = linkActivation.split('-').length === 1;
      const result = isOnlyEmailInLink
        ? await onboardStore.getState().checkLinkActivation(
            { email },
            {
              errorInterceptor: () => true,
            },
          )
        : await onboardStore.getState().confirmLinkActivation(
            {
              linkActivation,
            },
            {
              errorInterceptor: () => true,
            },
          );
      const status = getResultStatus(result);
      const error = getResultError(result)?.toLowerCase();
      const shouldToastError = !(
        status === 400 && error?.includes('already activated')
      );

      const isSuccess =
        result &&
        typeof result === 'object' &&
        'success' in result &&
        result.success === true;
      if (
        isSuccess &&
        !isOnlyEmailInLink &&
        lastSuccessfulLinkRef.current !== linkActivation
      ) {
        lastSuccessfulLinkRef.current = linkActivation;
        onActivationSuccess?.('email', linkActivation);
      }

      handleErrorsAndTimer(
        result,
        undefined,
        (message) => {
          if (shouldToastError) {
            toast.error(message);
          }
        },
        setResendAfter,
      );
      setIsError(status === 401 || status === 404 || status === 406);
    })();
  }, [email, linkActivation, toast, onboardStore, onActivationSuccess]);

  const isDisabled = Boolean(resendAfter || isError);

  const handleResend = async () => {
    setIsError(false);
    await form.handleSubmit();
  };

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
    >
      <Title className={styles.header} color="accent" size="xs">
        {texts.title}
      </Title>

      <TextBlock
        size="s"
        color="noteContrast"
        className={styles.text}
        line="150"
      >
        {texts.subTitle}
      </TextBlock>

      <SubmitButtonWrapper
        forceDisabled={isDisabled ? true : undefined}
        form={form}
      >
        <Button
          className={styles.noTextSubmit}
          type="submit"
          variant="filled"
          color="primary"
        >
          {texts.submit}
        </Button>
      </SubmitButtonWrapper>

      <div className={styles.footer}>
        <div className={styles.bottomSingleLink}>
          <ButtonText
            size="xs"
            color="noteContrast"
            onClick={() => {
              void navigate({
                to: location.pathname,
                hash: EOnboardRoutes.login,
              });
            }}
          >
            {texts.back}
          </ButtonText>
        </div>

        <Expander />

        <ButtonText
          size="xs"
          color="accent"
          disabled={isDisabled}
          onClick={handleResend}
        >
          {texts.resend}
          {resendAfter ? (
            <>
              {' ' + texts.in + ' '}
              <Countdown
                date={resendAfter}
                daysInHours
                onComplete={() => setResendAfter(null)}
                renderer={timerMinutesRender}
              />
            </>
          ) : null}
        </ButtonText>
      </div>
    </form>
  );
};
